// One way to turn a picture into the JPEG the driver-identity endpoints read:
// longest edge capped at 1280px, quality 0.8. The driver app's camera and the
// manager app's license upload both send through here, so Textract sees the
// same kind of image whichever side captured it.
const MAX_EDGE = 1280;
const JPEG_QUALITY = 0.8;

/** Draw a video frame, image or bitmap of the given size into a capped JPEG. */
export function drawToJpeg(source, width, height) {
  return new Promise((resolve, reject) => {
    const scale = Math.min(1, MAX_EDGE / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    canvas
      .getContext("2d")
      .drawImage(source, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(
      (blob) => (blob ? resolve(blob) : reject(new Error("capture failed"))),
      "image/jpeg",
      JPEG_QUALITY,
    );
  });
}

/**
 * A picked or photographed image file as a capped JPEG. Rejects on a file the
 * browser cannot decode — an iPhone HEIC in desktop Chrome, a PDF — so the
 * caller can ask for a JPEG or PNG instead. The <img> decode applies the
 * photo's EXIF orientation, so a phone picture arrives upright.
 */
export function imageFileToJpeg(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      drawToJpeg(img, img.naturalWidth, img.naturalHeight)
        .then(resolve, reject)
        .finally(() => URL.revokeObjectURL(url));
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("unreadable image"));
    };
    img.src = url;
  });
}

/** A blob's bytes as bare base64 (no data: prefix), for a JSON request body. */
export function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () =>
      resolve(String(reader.result).replace(/^data:[^,]*,/, ""));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
